import { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, Receipt, Copy, Check, ExternalLink, RefreshCw } from 'lucide-react';
import {
  fetchPaymentsLedger,
  getChannelLabel,
  isStripePayment,
  stripeDashboardUrl,
  getPayoutStatusStyle,
  formatPayoutStatusLabel,
  refundSourceLabel,
  PaymentLedgerEntry,
} from '../../lib/paymentsLedger';
import StripeBalancePanel from './StripeBalancePanel';

type DateFilter = 'this-month' | 'last-month' | 'this-year' | 'all-time';

interface PaymentsLedgerTabProps {
  businessId: string;
  /** Open the related booking invoice (BetaPaymentsModal) when possible. */
  onOpenBookingInvoice?: (bookingId: string) => void;
  /** Show the "Sync Stripe" button (only when the Stripe integration is active). */
  stripeConnected?: boolean;
  /** Sync Stripe transactions; resolves when done so the ledger can refresh. */
  onSyncStripe?: () => Promise<void> | void;
}

function getDateRange(filter: DateFilter): { start: Date | null; end: Date | null } {
  const now = new Date();
  const startOfDay = (y: number, m: number, d: number) => new Date(y, m, d, 0, 0, 0, 0);
  const endOfDay = (y: number, m: number, d: number) => new Date(y, m, d, 23, 59, 59, 999);
  switch (filter) {
    case 'this-month':
      return { start: startOfDay(now.getFullYear(), now.getMonth(), 1), end: endOfDay(now.getFullYear(), now.getMonth(), now.getDate()) };
    case 'last-month': {
      const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
      const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const lastDay = new Date(y, m + 1, 0).getDate();
      return { start: startOfDay(y, m, 1), end: endOfDay(y, m, lastDay) };
    }
    case 'this-year':
      return { start: startOfDay(now.getFullYear(), 0, 1), end: endOfDay(now.getFullYear(), now.getMonth(), now.getDate()) };
    case 'all-time':
    default:
      return { start: null, end: null };
  }
}

const fmtMoney = (n: number) =>
  '$' + (Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtPct = (n: number) => `${(Math.round((n || 0) * 100) / 100)}%`;

const COUNTED = new Set(['completed', 'succeeded', 'paid']);

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-green-100 text-green-700',
  succeeded: 'bg-green-100 text-green-700',
  paid: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-200 text-gray-700',
  voided: 'bg-gray-200 text-gray-500 line-through',
};

function targetLabel(entry: PaymentLedgerEntry): string {
  const n = entry.target_number || '—';
  if (entry.entry_kind === 'refund') {
    const what = refundSourceLabel(entry.refund_source);
    return entry.target_number ? `${what} · Invoice #${entry.target_number}` : what;
  }
  switch (entry.target_type) {
    case 'invoice': return `Invoice #${n}`;
    case 'product_sale': return `Sale #${n}`;
    case 'proposal': return `Proposal: ${n}`;
    case 'gift_card': return n.toLowerCase().startsWith('gift card') ? n : 'Gift Card';
    case 'subscription': return 'Subscription';
    default: return n;
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** A monospace ID with a one-click copy button. */
function CopyableId({ label, value, href }: { label: string; value: string; href?: string | null }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className="font-mono text-[11px] text-gray-700 truncate">{value}</span>
      <button type="button" onClick={copy} className="text-gray-400 hover:text-gray-700 flex-shrink-0" title="Copy">
        {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
      </button>
      {href && (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 flex-shrink-0" title="Open in Stripe">
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

/** Compact label-value pair: value sits directly beside the label for easy scanning. */
function KV({ label, value, valueClass = 'text-gray-900 font-medium' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className={`${valueClass} truncate`}>{value}</span>
    </div>
  );
}

export default function PaymentsLedgerTab({ businessId, onOpenBookingInvoice, stripeConnected, onSyncStripe }: PaymentsLedgerTabProps) {
  const [entries, setEntries] = useState<PaymentLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<DateFilter>('this-month');
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    let active = true;
    if (!businessId) return;
    setLoading(true);
    setError(null);
    const { start, end } = getDateRange(dateFilter);
    fetchPaymentsLedger(businessId, { start, end })
      .then((data) => { if (active) setEntries(data); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : 'Failed to load payments'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [businessId, dateFilter, reloadKey]);

  const handleSyncStripe = async () => {
    if (!onSyncStripe) return;
    setSyncing(true);
    try {
      await onSyncStripe();
      setReloadKey((k) => k + 1);
    } finally {
      setSyncing(false);
    }
  };

  const channelsPresent = useMemo(() => {
    const set = new Set<string>();
    entries.forEach((e) => set.add(e.channel || 'other'));
    return Array.from(set).sort();
  }, [entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (channelFilter !== 'all' && (e.channel || 'other') !== channelFilter) return false;
      if (statusFilter === 'refund') {
        if (e.entry_kind !== 'refund') return false;
      } else if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      if (q) {
        const hay = [
          e.customer_name,
          targetLabel(e),
          e.reference,
          e.target_number,
          String(e.amount),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, channelFilter, statusFilter, search]);

  const summary = useMemo(() => {
    let collected = 0, tips = 0, fees = 0, count = 0;
    const byChannel: Record<string, number> = {};
    for (const e of filtered) {
      if (!COUNTED.has(e.status)) continue;
      collected += Number(e.amount) || 0;
      tips += Number(e.tip_amount) || 0;
      fees += Number(e.surcharge_amount) || 0;
      count += 1;
      const ch = e.channel || 'other';
      byChannel[ch] = (byChannel[ch] || 0) + (Number(e.amount) || 0);
    }
    return { collected, tips, fees, count, byChannel };
  }, [filtered]);

  return (
    <div className="pb-8" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif' }}>
      {/* Live Stripe balance overview (only when Stripe is connected) */}
      {stripeConnected && <StripeBalancePanel businessId={businessId} refreshKey={reloadKey} />}

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 py-4">
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Collected</p>
          <p className="text-[16px] font-bold text-green-600 truncate">{fmtMoney(summary.collected)}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Payments</p>
          <p className="text-[16px] font-bold text-gray-900 truncate">{summary.count}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Tips</p>
          <p className="text-[16px] font-bold text-gray-900 truncate">{fmtMoney(summary.tips)}</p>
        </div>
        <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 min-w-0">
          <p className="text-[11px] font-medium text-gray-600 truncate">Processing Fees</p>
          <p className="text-[16px] font-bold text-amber-600 truncate">{fmtMoney(summary.fees)}</p>
        </div>
      </div>

      {/* By-channel chips */}
      {Object.keys(summary.byChannel).length > 0 && (
        <div className="flex flex-wrap gap-2 px-4 pb-2">
          {Object.entries(summary.byChannel)
            .sort((a, b) => b[1] - a[1])
            .map(([ch, amt]) => (
              <span key={ch} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-[12px] text-gray-700">
                <span className="font-medium">{getChannelLabel(ch)}</span>
                <span className="text-gray-500">{fmtMoney(amt)}</span>
              </span>
            ))}
        </div>
      )}

      {/* Filters */}
      <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer, invoice #, amount, reference"
            className="w-full pl-9 pr-3 py-2.5 bg-gray-100 border-0 rounded-[10px] text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
          />
        </div>
        <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value as DateFilter)}
          className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
          <option value="this-month">This Month</option>
          <option value="last-month">Last Month</option>
          <option value="this-year">This Year</option>
          <option value="all-time">All Time</option>
        </select>
        <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
          className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
          <option value="all">All Methods</option>
          {channelsPresent.map((ch) => <option key={ch} value={ch}>{getChannelLabel(ch)}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="refund">Refunds</option>
          <option value="refunded">Refunded</option>
          <option value="voided">Voided</option>
          <option value="failed">Failed</option>
        </select>
        {stripeConnected && onSyncStripe && (
          <button
            type="button"
            onClick={handleSyncStripe}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-[10px] bg-gray-900 text-white text-[14px] font-semibold disabled:opacity-50 active:scale-[0.98] transition-all"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync Stripe'}
          </button>
        )}
      </div>

      {/* List */}
      <div className="px-4">
        {loading ? (
          <div className="py-16 text-center text-gray-500 text-sm">Loading payments…</div>
        ) : error ? (
          <div className="py-10 text-center text-red-600 text-sm">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <Receipt className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No payments for this period.</p>
          </div>
        ) : (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden divide-y divide-gray-100">
            {filtered.map((e) => {
              const expanded = expandedId === e.id;
              const isBooking = e.target_type === 'invoice' && !!e.booking_id;
              // Fee breakdown: what we charged the customer (surcharge) vs what Stripe charged us.
              const customerSurcharge = Number(e.surcharge_amount) || 0;
              const preSurcharge = (Number(e.amount) || 0) - customerSurcharge;
              const customerPct = preSurcharge > 0 ? (customerSurcharge / preSurcharge) * 100 : 0;
              const stripeFee = e.stripe_fee != null ? Number(e.stripe_fee) : null;
              const stripePct = stripeFee != null && Number(e.amount) > 0 ? (stripeFee / Number(e.amount)) * 100 : 0;
              const feeNet = stripeFee != null ? customerSurcharge - stripeFee : null;
              return (
                <div key={e.id}>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : e.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                  >
                    {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-semibold text-gray-900 truncate">{e.customer_name || 'Unknown'}</span>
                        <span className="text-[12px] text-gray-500 truncate">· {targetLabel(e)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[12px] text-gray-500">{formatDateTime(e.created_at)}</span>
                        <span className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-[11px] font-medium text-gray-600">{getChannelLabel(e.channel)}</span>
                        {e.entry_kind === 'refund' ? (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-rose-50 text-[11px] font-medium text-rose-600">Refund</span>
                        ) : e.payment_type && e.payment_type !== 'final' && (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-blue-50 text-[11px] font-medium text-blue-600 capitalize">{e.payment_type}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-[15px] font-bold ${e.entry_kind === 'refund' ? 'text-rose-600' : 'text-gray-900'}`}>
                        {e.entry_kind === 'refund' ? `−${fmtMoney(Math.abs(e.amount))}` : fmtMoney(e.amount)}
                      </div>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium capitalize ${STATUS_STYLES[e.status] || 'bg-gray-100 text-gray-600'}`}>
                        {e.entry_kind === 'refund' && e.status === 'pending' ? 'processing' : e.status}
                      </span>
                    </div>
                  </button>

                  {expanded && e.entry_kind === 'refund' && (
                    <div className="px-4 pb-3 pl-11 text-[12px] animate-[fadeIn_0.12s_ease-out] space-y-1">
                      <KV label="Refunded" value={`−${fmtMoney(Math.abs(e.amount))}`} valueClass="text-rose-600 font-semibold" />
                      {e.refund_reason && <KV label="Reason" value={e.refund_reason} valueClass="text-gray-700" />}
                      {e.reference && <KV label="Reference" value={e.reference} valueClass="text-gray-700 font-mono text-[11px]" />}
                      {e.stripe_refund_id && <CopyableId label="Stripe refund" value={e.stripe_refund_id} />}
                      {e.refund_destination === 'card' && e.status === 'pending' && (
                        <p className="text-[11px] text-amber-700">Waiting on Stripe — if the network declines it, the amount is credited to the customer's wallet.</p>
                      )}
                    </div>
                  )}
                  {expanded && e.entry_kind !== 'refund' && (
                    <div className="px-4 pb-3 pl-11 text-[12px] animate-[fadeIn_0.12s_ease-out]">
                      {/* Money + fee breakdown — compact 2-col grid, value beside label */}
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                        <KV label="Principal" value={fmtMoney(e.principal_amount)} />
                        {Number(e.tip_amount) > 0 && <KV label="Tip" value={fmtMoney(e.tip_amount)} />}
                        {customerSurcharge > 0 && (
                          <KV label={`Surcharge${customerPct > 0 ? ` (${fmtPct(customerPct)})` : ''}`} value={fmtMoney(customerSurcharge)} />
                        )}
                        <KV label="Total" value={fmtMoney(e.amount)} valueClass="text-gray-900 font-semibold" />
                        {stripeFee != null && (
                          <KV label={`Stripe fee${stripePct > 0 ? ` (${fmtPct(stripePct)})` : ''}`} value={fmtMoney(stripeFee)} valueClass="text-amber-600 font-medium" />
                        )}
                        {feeNet != null && (
                          <KV
                            label="Net on fees"
                            value={`${feeNet >= 0 ? '+' : '−'}${fmtMoney(Math.abs(feeNet))} ${feeNet >= 0 ? 'profit' : 'cost'}`}
                            valueClass={feeNet >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}
                          />
                        )}
                        {e.stripe_net != null && <KV label="Net deposited" value={fmtMoney(Number(e.stripe_net))} />}
                      </div>

                      {/* Stripe IDs + payout */}
                      {isStripePayment(e) && (
                        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="flex items-center gap-1.5 text-gray-700 font-semibold">
                              <img src="/Stripe (2).png" alt="Stripe" className="h-4 w-4 object-contain" /> Stripe
                            </span>
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${getPayoutStatusStyle(e.payout_status)}`}>
                              {formatPayoutStatusLabel(e.payout_status)}
                            </span>
                            {e.payout_arrival_date && (
                              <span className="text-[11px] text-gray-500">Arrives {formatDate(e.payout_arrival_date)}</span>
                            )}
                          </div>
                          {e.stripe_payment_intent_id && (
                            <CopyableId label="Payment" value={e.stripe_payment_intent_id} href={stripeDashboardUrl(e.stripe_payment_intent_id)} />
                          )}
                          {e.balance_transaction_id && <CopyableId label="Balance" value={e.balance_transaction_id} />}
                          {e.stripe_payout_id && <CopyableId label="Payout" value={e.stripe_payout_id} />}
                          {stripeFee == null && (
                            <div className="text-[11px] text-gray-400">Sync Stripe transactions to see the fee Stripe charged us.</div>
                          )}
                        </div>
                      )}

                      {!isStripePayment(e) && e.reference && (
                        <div className="mt-1.5 flex items-baseline gap-1.5">
                          <span className="text-gray-500">Reference</span>
                          <span className="text-gray-700 font-mono text-[11px] truncate">{e.reference}</span>
                        </div>
                      )}

                      {isBooking && onOpenBookingInvoice && (
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => onOpenBookingInvoice(e.booking_id!)}
                            className="text-[13px] font-medium text-blue-600 hover:text-blue-700"
                          >
                            View invoice →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
